"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TRUNCATE_THRESHOLD } from "@/lib/constants";
import { cn } from "@/lib/utils";

interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Seleccionar opción...",
  searchPlaceholder = "Buscar...",
  emptyMessage = "No se encontraron opciones",
  className,
  disabled = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{
          width: "var(--radix-popover-trigger-width)",
          maxWidth: "var(--radix-popover-content-available-width)",
        }}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isTruncated = option.label.length > TRUNCATE_THRESHOLD;

                const itemContent = (
                  <div className="flex min-w-0 items-center gap-2 w-full px-2 py-1.5">
                    <span className="truncate text-left">{option.label}</span>
                    <Check
                      className={cn(
                        "ml-auto shrink-0",
                        value === option.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </div>
                );

                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    className="p-0"
                    onSelect={(currentLabel) => {
                      const sel = options.find(
                        (opt) => opt.label === currentLabel,
                      );
                      const selectedValue = sel?.value ?? "";
                      onValueChange(
                        selectedValue === value ? "" : selectedValue,
                      );
                      setOpen(false);
                    }}
                  >
                    {isTruncated ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          {option.label}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      itemContent
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
